import * as React from 'react';
import { useGlobalState, GlobalState } from 'piral-core';
import { isChristmasTime } from '../SnowFallButton/SnowfallButton';

interface PileConfig {
  center: number;
  width: number;
  maxHeight: number;
}

interface SnowDrift {
  heights: number[];
  targetHeights: number[];
  bumpOffsets: number[];
  bumpPhases: number[];
  pileConfigs: PileConfig[];
}

interface TileRect {
  top: number;
  left: number;
  right: number;
  width: number;
}

// Constants
const DRIFT_RESOLUTION = 60;
const MAX_DRIFT_HEIGHT = 14;
const BASE_GROWTH_RATE = 0.003;
const SNOW_SPAWN_CHANCE = 0.3;
const SPARKLE_COUNT = 8;
const VIEWPORT_BUFFER = 50;
const DELTA_TIME_CLAMP = 0.1;
const DEFAULT_DELTA_TIME = 0.016;
const MIN_PILES = 3;
const MAX_ADDITIONAL_PILES = 3;

// Derive snow level from snowflakeCount (level * level * 500)
const getSnowLevel = (snowflakeCount: number): number => {
  if (!snowflakeCount || snowflakeCount <= 0) return 0;
  return Math.round(Math.sqrt(snowflakeCount / 500));
};

// Create random pile configurations for a tile
const createPileConfigs = (): PileConfig[] => {
  const piles: PileConfig[] = [];
  const numPiles = MIN_PILES + Math.floor(Math.random() * MAX_ADDITIONAL_PILES);

  for (let i = 0; i < numPiles; i++) {
    piles.push({
      center: 0.15 + Math.random() * 0.7,
      width: 0.08 + Math.random() * 0.15,
      maxHeight: 0.5 + Math.random() * 0.5,
    });
  }

  return piles;
};

// Create empty initial profile (starts at zero)
const createInitialProfile = (): number[] => {
  return new Array(DRIFT_RESOLUTION).fill(0);
};

// Create small bumps for surface texture
const createBumpOffsets = (): number[] => {
  const bumps: number[] = [];
  for (let i = 0; i < DRIFT_RESOLUTION; i++) {
    bumps.push((Math.random() - 0.5) * 2);
  }
  return bumps;
};

const createBumpPhases = (): number[] => {
  const phases: number[] = [];
  for (let i = 0; i < DRIFT_RESOLUTION; i++) {
    phases.push(Math.random() * Math.PI * 2);
  }
  return phases;
};

// Smooth interpolation
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export const SnowAccumulation: React.FC = () => {
  const settings = useGlobalState((s: GlobalState) => s.snowSettings);
  const snowPileEnabled = useGlobalState((s: GlobalState) => s.snowPileEnabled);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const driftsRef = React.useRef<Map<number, SnowDrift>>(new Map());
  const tileRectsRef = React.useRef<TileRect[]>([]);
  const animationRef = React.useRef<number>(0);
  const lastUpdateRef = React.useRef<number>(Date.now());

  const updateTileRects = React.useCallback(() => {
    const tiles = document.querySelectorAll('.neo-dashboard-tile');
    tileRectsRef.current = Array.from(tiles).map(tile => {
      const rect = tile.getBoundingClientRect();
      return {
        top: rect.top + window.scrollY,
        left: rect.left,
        right: rect.right,
        width: rect.width,
      };
    });

    // Clear drifts for tiles that no longer exist
    if (tiles.length === 0) {
      driftsRef.current.clear();
    }
  }, []);

  const render = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const now = Date.now();
    let deltaTime = (now - lastUpdateRef.current) / 1000;
    lastUpdateRef.current = now;

    // Clamp deltaTime to avoid jumps when tab is inactive
    if (deltaTime > DELTA_TIME_CLAMP) {
      deltaTime = DEFAULT_DELTA_TIME;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Don't render if no tiles (e.g., navigated away from dashboard)
    if (tileRectsRef.current.length === 0) {
      animationRef.current = requestAnimationFrame(render);
      return;
    }

    const scrollY = window.scrollY;
    const isDark = document.documentElement.classList.contains('dark');

    // Calculate growth rate based on snow level (quadratic scaling for dramatic difference)
    const snowLevel = getSnowLevel(settings?.snowflakeCount || 0);
    const growthRate = BASE_GROWTH_RATE * (1 + snowLevel * snowLevel * 0.15);

    tileRectsRef.current.forEach((tile, index) => {
      let drift = driftsRef.current.get(index);

      // Initialize drift if needed
      if (!drift) {
        const pileConfigs = createPileConfigs();
        drift = {
          heights: createInitialProfile(),
          targetHeights: createInitialProfile(),
          bumpOffsets: createBumpOffsets(),
          bumpPhases: createBumpPhases(),
          pileConfigs,
        };
        driftsRef.current.set(index, drift);
      }

      // Grow snow - add snow at random pile locations
      if (Math.random() < SNOW_SPAWN_CHANCE) {
        // Pick a random pile to add snow to
        const randomPile = drift.pileConfigs[Math.floor(Math.random() * drift.pileConfigs.length)];

        // Pick a spot near this pile's center with some randomness
        const pileSpread = randomPile.width * (Math.random() - 0.5) * 1.5;
        const targetNormX = Math.max(0.05, Math.min(0.95, randomPile.center + pileSpread));
        const targetIndex = Math.floor(targetNormX * (DRIFT_RESOLUTION - 1));

        const normalizedX = targetIndex / (DRIFT_RESOLUTION - 1);
        const edgeFalloff = Math.sin(normalizedX * Math.PI);

        // Calculate max height based on pile config and distance from pile center
        const distFromPileCenter = Math.abs(normalizedX - randomPile.center);
        const pileInfluence = distFromPileCenter < randomPile.width
          ? Math.cos((distFromPileCenter / randomPile.width) * Math.PI * 0.5)
          : 0.2;

        // Random chance based on position and growth rate
        if (Math.random() < growthRate * 15 * pileInfluence) {
          // Add varying amount of snow
          const snowAmount = 0.15 + Math.random() * 0.35;
          const maxAtPoint = edgeFalloff * randomPile.maxHeight * MAX_DRIFT_HEIGHT;
          drift.targetHeights[targetIndex] = Math.min(drift.targetHeights[targetIndex] + snowAmount, maxAtPoint);

          // Spread to neighbors with random falloff (smaller spread for distinct piles)
          const spreadRadius = 1 + Math.floor(Math.random() * 2);
          for (let offset = 1; offset <= spreadRadius; offset++) {
            const falloff = 1 - (offset / (spreadRadius + 1));
            const neighborAmount = snowAmount * falloff * (0.2 + Math.random() * 0.3);

            if (targetIndex - offset >= 0) {
              const leftNormX = (targetIndex - offset) / (DRIFT_RESOLUTION - 1);
              const leftDistFromPile = Math.abs(leftNormX - randomPile.center);
              const leftPileInfluence = leftDistFromPile < randomPile.width ? randomPile.maxHeight : 0.3;
              const leftMax = Math.sin(leftNormX * Math.PI) * leftPileInfluence * MAX_DRIFT_HEIGHT;
              drift.targetHeights[targetIndex - offset] = Math.min(
                drift.targetHeights[targetIndex - offset] + neighborAmount,
                leftMax
              );
            }
            if (targetIndex + offset < DRIFT_RESOLUTION) {
              const rightNormX = (targetIndex + offset) / (DRIFT_RESOLUTION - 1);
              const rightDistFromPile = Math.abs(rightNormX - randomPile.center);
              const rightPileInfluence = rightDistFromPile < randomPile.width ? randomPile.maxHeight : 0.3;
              const rightMax = Math.sin(rightNormX * Math.PI) * rightPileInfluence * MAX_DRIFT_HEIGHT;
              drift.targetHeights[targetIndex + offset] = Math.min(
                drift.targetHeights[targetIndex + offset] + neighborAmount,
                rightMax
              );
            }
          }
        }
      }

      // Update all points
      for (let i = 0; i < DRIFT_RESOLUTION; i++) {
        // Smoothly animate current heights toward target
        drift.heights[i] = lerp(drift.heights[i], drift.targetHeights[i], deltaTime * 1.5);

        // Animate bump phases for subtle surface movement
        drift.bumpPhases[i] += deltaTime * (0.3 + Math.random() * 0.3);
      }

      // Draw snow
      const tileTop = tile.top - scrollY;

      // Skip tiles not in viewport
      if (tileTop < -VIEWPORT_BUFFER || tileTop > window.innerHeight + VIEWPORT_BUFFER) return;

      // Snow drift margins
      const driftMargin = tile.width * 0.12;
      const driftWidth = tile.width - driftMargin * 2;

      // Create main snow body gradient
      const driftGradient = ctx.createLinearGradient(
        tile.left,
        tileTop - MAX_DRIFT_HEIGHT,
        tile.left,
        tileTop + 3
      );

      if (isDark) {
        driftGradient.addColorStop(0, 'rgba(235, 240, 250, 0.97)');
        driftGradient.addColorStop(0.3, 'rgba(220, 228, 242, 0.95)');
        driftGradient.addColorStop(0.7, 'rgba(200, 212, 230, 0.92)');
        driftGradient.addColorStop(1, 'rgba(180, 195, 218, 0.88)');
      } else {
        // Subtle off-white for visibility in light mode
        driftGradient.addColorStop(0, 'rgba(245, 247, 250, 0.99)');
        driftGradient.addColorStop(0.3, 'rgba(238, 240, 245, 0.97)');
        driftGradient.addColorStop(0.7, 'rgba(228, 232, 240, 0.94)');
        driftGradient.addColorStop(1, 'rgba(218, 224, 235, 0.9)');
      }

      // Draw main snow mass with smooth curves
      ctx.beginPath();

      // Start at bottom left
      const startX = tile.left + driftMargin;
      ctx.moveTo(startX, tileTop);

      // Draw the top surface with bumps for texture
      for (let i = 0; i < DRIFT_RESOLUTION; i++) {
        const x = tile.left + driftMargin + (driftWidth * i) / (DRIFT_RESOLUTION - 1);
        const bumpAmount = Math.sin(drift.bumpPhases[i]) * drift.bumpOffsets[i] * 0.5;
        const y = tileTop - drift.heights[i] - bumpAmount;

        if (i === 0) {
          ctx.lineTo(x, y);
        } else {
          // Use bezier curves for smooth, organic look
          const prevX = tile.left + driftMargin + (driftWidth * (i - 1)) / (DRIFT_RESOLUTION - 1);
          const prevBump = Math.sin(drift.bumpPhases[i - 1]) * drift.bumpOffsets[i - 1] * 0.5;
          const prevY = tileTop - drift.heights[i - 1] - prevBump;

          const cpX = (prevX + x) / 2;
          const cpY = (prevY + y) / 2;
          ctx.quadraticCurveTo(prevX, prevY, cpX, cpY);
        }
      }

      // Close the path along the bottom
      ctx.lineTo(tile.left + driftMargin + driftWidth, tileTop);
      ctx.closePath();

      // Fill main snow body
      ctx.fillStyle = driftGradient;
      ctx.fill();

      // Add highlight layer on top for fresh snow look
      ctx.beginPath();
      ctx.moveTo(startX, tileTop);

      for (let i = 0; i < DRIFT_RESOLUTION; i++) {
        const x = tile.left + driftMargin + (driftWidth * i) / (DRIFT_RESOLUTION - 1);
        const bumpAmount = Math.sin(drift.bumpPhases[i]) * drift.bumpOffsets[i] * 0.5;
        // Highlight sits on top third of snow
        const highlightHeight = drift.heights[i] * 0.4;
        const y = tileTop - drift.heights[i] - bumpAmount + highlightHeight;

        if (i === 0) {
          ctx.lineTo(x, tileTop - drift.heights[i] - bumpAmount);
        } else {
          const prevX = tile.left + driftMargin + (driftWidth * (i - 1)) / (DRIFT_RESOLUTION - 1);
          const prevBump = Math.sin(drift.bumpPhases[i - 1]) * drift.bumpOffsets[i - 1] * 0.5;
          const prevHighlightY = tileTop - drift.heights[i - 1] - prevBump;

          ctx.quadraticCurveTo(prevX, prevHighlightY, (prevX + x) / 2, (prevHighlightY + y) / 2);
        }
      }

      // Create highlight gradient
      const highlightGradient = ctx.createLinearGradient(
        tile.left,
        tileTop - MAX_DRIFT_HEIGHT,
        tile.left,
        tileTop - MAX_DRIFT_HEIGHT * 0.5
      );
      highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
      highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

      ctx.lineTo(tile.left + driftMargin + driftWidth, tileTop);
      ctx.closePath();
      ctx.fillStyle = highlightGradient;
      ctx.fill();

      // Add subtle sparkles embedded in the snow
      const time = now * 0.001;
      for (let i = 0; i < SPARKLE_COUNT; i++) {
        const sparkleX = tile.left + driftMargin + driftWidth * (0.1 + 0.8 * ((i * 0.618) % 1));
        const pointIndex = Math.floor(((sparkleX - tile.left - driftMargin) / driftWidth) * (DRIFT_RESOLUTION - 1));
        const clampedIndex = Math.max(0, Math.min(DRIFT_RESOLUTION - 1, pointIndex));
        const sparkleY = tileTop - drift.heights[clampedIndex] * (0.3 + 0.5 * Math.random());

        const sparklePhase = time * 2 + i * 1.5;
        const sparkleIntensity = Math.max(0, Math.sin(sparklePhase));

        if (sparkleIntensity > 0.6) {
          ctx.fillStyle = `rgba(255, 255, 255, ${sparkleIntensity * 0.5})`;
          ctx.beginPath();
          ctx.arc(sparkleX, sparkleY, 1 + sparkleIntensity, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    });

    animationRef.current = requestAnimationFrame(render);
  }, [settings]);

  // Static render function for when animation is paused (draws existing snow without growth)
  const renderStatic = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Don't render if no tiles (e.g., navigated away from dashboard)
    if (tileRectsRef.current.length === 0) return;

    const scrollY = window.scrollY;
    const isDark = document.documentElement.classList.contains('dark');

    tileRectsRef.current.forEach((tile, index) => {
      const drift = driftsRef.current.get(index);
      if (!drift) return;

      const tileTop = tile.top - scrollY;
      if (tileTop < -VIEWPORT_BUFFER || tileTop > window.innerHeight + VIEWPORT_BUFFER) return;

      const driftMargin = tile.width * 0.12;
      const driftWidth = tile.width - driftMargin * 2;

      const driftGradient = ctx.createLinearGradient(
        tile.left,
        tileTop - MAX_DRIFT_HEIGHT,
        tile.left,
        tileTop + 3
      );

      if (isDark) {
        driftGradient.addColorStop(0, 'rgba(235, 240, 250, 0.97)');
        driftGradient.addColorStop(0.3, 'rgba(220, 228, 242, 0.95)');
        driftGradient.addColorStop(0.7, 'rgba(200, 212, 230, 0.92)');
        driftGradient.addColorStop(1, 'rgba(180, 195, 218, 0.88)');
      } else {
        driftGradient.addColorStop(0, 'rgba(245, 247, 250, 0.99)');
        driftGradient.addColorStop(0.3, 'rgba(238, 240, 245, 0.97)');
        driftGradient.addColorStop(0.7, 'rgba(228, 232, 240, 0.94)');
        driftGradient.addColorStop(1, 'rgba(218, 224, 235, 0.9)');
      }

      ctx.beginPath();
      const startX = tile.left + driftMargin;
      ctx.moveTo(startX, tileTop);

      for (let i = 0; i < DRIFT_RESOLUTION; i++) {
        const x = tile.left + driftMargin + (driftWidth * i) / (DRIFT_RESOLUTION - 1);
        const y = tileTop - drift.heights[i];

        if (i === 0) {
          ctx.lineTo(x, y);
        } else {
          const prevX = tile.left + driftMargin + (driftWidth * (i - 1)) / (DRIFT_RESOLUTION - 1);
          const prevY = tileTop - drift.heights[i - 1];
          const cpX = (prevX + x) / 2;
          const cpY = (prevY + y) / 2;
          ctx.quadraticCurveTo(prevX, prevY, cpX, cpY);
        }
      }

      ctx.lineTo(tile.left + driftMargin + driftWidth, tileTop);
      ctx.closePath();
      ctx.fillStyle = driftGradient;
      ctx.fill();
    });
  }, []);

  React.useEffect(() => {
    const snowLevel = getSnowLevel(settings?.snowflakeCount || 0);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      updateTileRects();
    };

    // Always set up resize and scroll handlers so snow follows tiles
    handleResize();
    window.addEventListener('resize', handleResize);

    if (!isChristmasTime() || !settings || snowLevel === 0) {
      // Stop growth animation but redraw on scroll to keep snow with tiles
      cancelAnimationFrame(animationRef.current);

      const handleScroll = () => {
        updateTileRects();
        renderStatic();
      };

      window.addEventListener('scroll', handleScroll, true);
      renderStatic(); // Initial static render

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }

    // Active snowfall - run animation
    window.addEventListener('scroll', updateTileRects, true);

    // Initial tile positions after a short delay to let tiles render
    const timeoutId = setTimeout(updateTileRects, 100);

    render();

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', updateTileRects, true);
      clearTimeout(timeoutId);
    };
  }, [settings, snowPileEnabled, updateTileRects, render, renderStatic]);

  // Pause animation when tab is hidden to save resources
  React.useEffect(() => {
    const snowLevel = getSnowLevel(settings?.snowflakeCount || 0);
    if (!isChristmasTime() || !settings || snowLevel === 0) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        cancelAnimationFrame(animationRef.current);
      } else {
        lastUpdateRef.current = Date.now();
        render();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [settings, render]);

  // Watch for DOM changes to detect route navigation (tiles appearing/disappearing)
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let hadTiles = false;

    const clearCanvas = () => {
      driftsRef.current.clear();
      tileRectsRef.current = [];
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    const checkTiles = () => {
      const tiles = document.querySelectorAll('.neo-dashboard-tile');
      if (tiles.length === 0) {
        hadTiles = false;
        clearCanvas();
      } else if (!hadTiles) {
        // Tiles appeared - update rects to start rendering
        hadTiles = true;
        updateTileRects();
      }
    };

    const observer = new MutationObserver(checkTiles);

    // Observe the app content area for child changes
    const appContent = document.getElementById('app-content');
    if (appContent) {
      observer.observe(appContent, { childList: true, subtree: true });
    }

    // Also observe document body for broader route changes
    observer.observe(document.body, { childList: true, subtree: true });

    // Check immediately
    checkTiles();

    return () => observer.disconnect();
  }, [updateTileRects]);

  if (!isChristmasTime() || !settings || !snowPileEnabled) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 800,
      }}
    />
  );
};
